<div
    class="dialog-container sai-dialog-shell add-data-sources-dialog"
    [dir]="lang === 'en' ? 'ltr' : 'rtl'"
>
    <div class="dialog-loading-overlay" *ngIf="isLoadingDataSourceData">
        <mat-progress-spinner
            mode="indeterminate"
            diameter="52"
        ></mat-progress-spinner>
    </div>

    <ng-container *transloco="let t">
        <app-sai-dialog-header
            icon="heroicons_outline:square-3-stack-3d"
            (closed)="close()"
        >
            <ng-container saiDialogHeaderTitle>
                {{ t(data.type) }} {{ t("Data Source") }}
            </ng-container>
            <div class="header-subtitle" *ngIf="data.type === 'Add'" saiDialogHeaderSubtitle>
                {{ t("Configure data source settings and connection") }}
            </div>
            <div
                class="header-subtitle"
                *ngIf="data.type !== 'Add' && form.get('name')?.value"
                saiDialogHeaderSubtitle
            >
                {{ t("Data Source") }}:
                <strong>{{ form.get("name")?.value }}</strong>
            </div>
        </app-sai-dialog-header>

        <form [formGroup]="form" class="add-data-sources-dialog-form">
            <div class="add-data-sources-dialog-body" cdkScrollable>
                <div
                    class="add-data-sources-form-fields grid grid-cols-1 lg:grid-cols-3 gap-4"
                >
            <div
                class="col-span-1 sai-tour-field"
                data-sai-tour="ds-field-name"
            >
                <mat-form-field class="w-full relative fuse-mat-dense">
                    <mat-label>{{ t("Data Source Name") }}</mat-label>

                    <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                        [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                            t(
                                'The unique identifier assigned to this data source for reference across agents and modules'
                            )
                        ">
                        error_outline
                    </mat-icon>

                    <input matInput type="text" maxlength="50" formControlName="name"
                        [placeholder]="t('Enter data source name')" />

                    <mat-hint align="end">
                        {{ form.get("name")?.value?.length || 0 }}/50
                    </mat-hint>
                    <mat-error *ngIf="
                            form.get('name')?.touched &&
                            form.get('name')?.hasError('required')
                        " class="text-red-600">
                        {{ t("Data source name is required") }}.
                    </mat-error>
    
                    <mat-error *ngIf="
                            form.get('name')?.touched &&
                            form.get('name')?.hasError('maxlength')
                        " class="text-red-600">
                        {{ t("Data source name must be less than 50 characters") }}.
                    </mat-error>
                </mat-form-field>

            </div>

            <div
                class="col-span-1 sai-tour-field"
                data-sai-tour="ds-field-department"
            >
                <mat-form-field class="w-full relative fuse-mat-dense add-ds-dept-field">
                    <mat-label>{{ t("Data Source Department") }}</mat-label>
                    <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                        [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                            canShareWithDepartments
                                ? t(
                                      'Select departments for this data source. Use the share toggle beside each selected department to control access.'
                                  )
                                : t(
                                      'Indicates which department owns or primarily uses this data source within the organization.'
                                  )
                        ">error_outline</mat-icon>
                    <mat-select
                        [formControlName]="'department'"
                        [multiple]="true"
                        panelClass="add-ds-dept-select-panel"
                    >
                        <mat-option
                            *ngFor="
                                let department of departmenList;
                                trackBy: trackByFn
                            "
                            [value]="department.id"
                            class="add-ds-dept-option"
                        >
                            <span class="add-ds-dept-option__row">
                                <span class="add-ds-dept-option__name">{{
                                    department.name
                                }}</span>
                                <mat-slide-toggle
                                    *ngIf="canShareWithDepartments"
                                    class="add-ds-dept-option__share"
                                    [checked]="isDepartmentShared(department.id)"
                                    [disabled]="
                                        department.id == null ||
                                        !assignedDepartmentIds.includes(+department.id) ||
                                        data.type === 'View' ||
                                        shareSaving
                                    "
                                    (click)="$event.stopPropagation()"
                                    (change)="onDepartmentShareToggle(department.id, $event)"
                                    color="primary"
                                ></mat-slide-toggle>
                            </span>
                        </mat-option>
                    </mat-select>
                    <mat-hint
                        *ngIf="canShareWithDepartments && shareDepartmentsTotal"
                        align="start"
                    >
                        {{ sharedDepartmentsCount }}/{{ shareDepartmentsTotal }}
                        {{ t('departments shared') }}
                    </mat-hint>
                    <mat-error *ngIf="
                            form.get('department').touched &&
                            form.get('department').hasError('required')
                        " class="text-red-600">
                        {{ t("Data source department is required") }}.
                    </mat-error>
                </mat-form-field>
            </div>

            <div
                *ngIf="data.type === 'Add' || data.type === 'Edit' || data.type === 'View'"
                class="col-span-1 sai-tour-field"
                data-sai-tour="ds-field-owner"
            >
                <mat-form-field class="w-full relative fuse-mat-dense">
                    <mat-label>{{ t("Owned By") }}</mat-label>
                    <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                        [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                            t(
                                'Select the manager responsible for this data source'
                            )
                        ">error_outline</mat-icon>
                    <mat-select
                        [formControlName]="'manager'"
                        [compareWith]="compareManagers"
                        [disabled]="
                            data.type === 'View' ||
                            (data.type === 'Add' && !assignedDepartmentIds.length)
                        "
                    >
                        <mat-option *ngFor="let member of membersList" [value]="member">
                            {{ member.name }}
                        </mat-option>
                    </mat-select>
                    <mat-error *ngIf="
                            form.get('manager').touched &&
                            form.get('manager').hasError('required')
                        " class="text-red-600">
                        {{ t("Manager is required") }}.
                    </mat-error>
                </mat-form-field>
            </div>

            <div
                class="col-span-1 sai-tour-field"
                data-sai-tour="ds-field-type"
            >
                <mat-form-field class="w-full relative fuse-mat-dense">
                    <mat-label>{{ t("Data Source Type") }}</mat-label>
                    <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                        [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                            t(
                                'Specifies whether the data source is internally maintained or externally integrated.'
                            )
                        ">error_outline</mat-icon>
                    <!--
                        No `[disabled]="!isAdmin"` here either (matches the fix already applied to
                        the AWS_S3/GOOGLE_DRIVE options below): opening this whole dialog already
                        required `canUseDataSource` (the actor's effective `data_source` permission
                        -- see `data-sources.component.ts`), which is the same generic authorization
                        the backend's `POST /new-data-sources` route enforces
                        (`authorizeUserOrMemberOrStaff`: User/Member/Staff all pass). Category
                        selection here must not layer a second, stricter, role-only gate on top of
                        that. Per-type restrictions (SAP/Snowflake/Oracle/PostgreSQL's `can_use_X`)
                        remain on their own options, unchanged.
                    -->
                    <mat-select [formControlName]="'type'">
                        <mat-option [value]="'internal'">{{
                            t("Internal Data Source")
                            }}</mat-option>

                        <mat-option [value]="'external'">{{
                            t("External Data Source")
                            }}</mat-option>
                    </mat-select>
                    <mat-hint *ngIf="typeChangeLocks.lockCategoryType" align="start">
                        {{ t(typeChangeLocks.categoryLockReason) }}
                    </mat-hint>
                    <mat-error *ngIf="
                            form.get('type').touched &&
                            form.get('type').hasError('required')
                        " class="text-red-600">
                        {{ t("Data source type is required") }}.
                    </mat-error>
                </mat-form-field>
            </div>

            <ng-container *ngIf="form.get('type').value === 'internal'">
                <div
                    class="col-span-1 sai-tour-field"
                    data-sai-tour="ds-field-datatype"
                >
                    <mat-form-field class="w-full relative fuse-mat-dense">
                        <mat-label>{{ t("Data Type") }}</mat-label>
                        <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                            [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                                t(
                                    'Describes the structure of the data in an internal data source.'
                                )
                            ">error_outline</mat-icon>
                        <mat-select [formControlName]="'internalType'">
                            <mat-option
                                [value]="'Structured Tables'"
                                [disabled]="typeChangeLocks.disableInternalTableMode"
                            >{{
                                t("Structured Table")
                                }}</mat-option>
                            <mat-option
                                [value]="'Unstructured Documents'"
                                [disabled]="typeChangeLocks.disableInternalFileMode"
                            >{{
                                t("Unstructured Document")
                                }}</mat-option>
                        </mat-select>
                        <mat-hint
                            *ngIf="typeChangeLocks.disableInternalFileMode"
                            align="start"
                        >
                            {{ t(typeChangeLocks.internalFileModeLockReason) }}
                        </mat-hint>
                        <mat-hint
                            *ngIf="typeChangeLocks.disableInternalTableMode"
                            align="start"
                        >
                            {{ t(typeChangeLocks.internalTableModeLockReason) }}
                        </mat-hint>
                        <mat-error *ngIf="
                            form.get('internalType').touched &&
                            form.get('internalType').hasError('required')
                        " class="text-red-600">
                            {{ t("Data Type is required") }}.
                        </mat-error>
                    </mat-form-field>
                </div>
            </ng-container>

            <ng-container *ngIf="form.get('type').value === 'external'">
                <div class="col-span-1">
                    <mat-form-field class="w-full relative fuse-mat-dense">
                        <mat-label>{{ t("Connection Type") }}</mat-label>
                        <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl"
                            [ngClass]="lang === 'en' ? 'right-0' : 'left-0'" [matTooltip]="
                                t(
                                    'Defines how the external data source is accessed, such as remote databases or remote containers.'
                                )
                            ">
                            error_outline
                        </mat-icon>

                        <mat-select [formControlName]="'externalType'">
                            <!-- Remote Database Group -->
                            <mat-optgroup [label]="t('Remote Database')" class="font-bold">
                                <mat-option [value]="'SAP'" [disabled]="!data.can_use_sap">{{
                                    t("SAP Hana") }}</mat-option>
                                <mat-option [value]="'Snowflake'"
                                    [disabled]="!data.can_use_snowflake">{{ t("Snowflake") }}</mat-option>
                                <mat-option [value]="'Oracle'"
                                    [disabled]="!data.can_use_oracle">{{
                                    t("Oracle")
                                    }}</mat-option>
                                <!-- Covers both the PostgreSQL and the Amazon RDS
                                     integrations: both save the same `remotepsql`
                                     credential via users/add-remote-postgres. -->
                                <mat-option [value]="'Remote Postgres'"
                                    [disabled]="!data.can_use_remote_postgres">{{
                                    t("PostgreSQL")
                                    }}</mat-option>
                            </mat-optgroup>

                            <!-- Remote Storage Container Group -->
                            <!--
                                No per-provider `[disabled]` gate here: creating a Media Container
                                Data Source (AWS S3 or Google Drive) follows the same generic
                                Data-Source-create authorization every other external/internal type
                                uses (`canUseDataSource` already gates opening this whole dialog;
                                the backend's `checkType` places AWS_S3/GOOGLE_DRIVE in the same
                                `allowedExternal` list as SAP/Snowflake/Oracle/PostgreSQL, with no
                                Role.user-only branch). Org-Admin-only applies to managing the
                                underlying connection/Service-Account credentials in Integrations,
                                a separate authorization surface this dialog never touches.
                            -->
                            <mat-optgroup [label]="t('Remote Storage Container')" class="font-bold">
                                <mat-option [value]="'AWS_S3'">{{ t("AWS S3") }}</mat-option>
                                <mat-option [value]="'GOOGLE_DRIVE'">{{
                                    t("Google Drive")
                                    }}</mat-option>
                            </mat-optgroup>
                        </mat-select>
                        <mat-hint *ngIf="typeChangeLocks.lockExternalProvider" align="start">
                            {{ t(typeChangeLocks.externalProviderLockReason) }}
                        </mat-hint>
                    </mat-form-field>
                </div>
            </ng-container>

            <!-- <ng-container *ngIf="form.get('type').value === 'external' && form.get('externalType').value === 'SAP'">
                <div class="w-full lg:w-[48%]">
                    <mat-form-field class="w-full relative fuse-mat-dense">
                        <mat-label>{{ t('Choose Tables') }}</mat-label>
                        <mat-select [formControlName]="'selectedTables'" [multiple]="true">
                            <mat-select-trigger>
                                {{ form.get('selectedTables').value?.join(', ') || t('Select tables') }}
                            </mat-select-trigger>
                            <mat-option>
                                <ngx-mat-select-search [formControl]="tableFilterCtrl" placeholderLabel="Search..."
                                    noEntriesFoundLabel="No results found">
                                </ngx-mat-select-search>
                            </mat-option>
                            <mat-optgroup *ngFor="let item of tables; index as i" [label]="item.nameLevel1"
                                class="level1">
                                <mat-option *ngFor="let value of item.valuesLevel1; index as j" [value]="value.value"
                                    class="level1-option relative">
                                    <div class="flex justify-between items-center">
                                        <span>{{ value.viewValue }}</span>
                                        <div class="flex items-center justify-center gap-2 absolute right-0">
                                            <button mat-icon-button class="sai-card-action-btn flex items-center justify-center"
                                                (click)="deleteExternalTable(value)">
                                                <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                                            </button>
                                        </div>
                                    </div>
                                </mat-option>
                            </mat-optgroup>
                        </mat-select>
                    </mat-form-field>
                </div>
            </ng-container> -->

            <!-- <ng-container *ngIf="form.get('type').value === 'external' && form.get('externalType').value === 'SAP'">
                <button [matTooltip]="t('Add SAP Table')" mat-icon-button (click)="createExternalTable('Add')">
                    <mat-icon class="text-gradient">add</mat-icon>
                </button>
            </ng-container> -->

            <mat-form-field class="col-span-1 lg:col-span-3 fuse-mat-dense" [subscriptSizing]="'dynamic'">
                <mat-label>{{ t("Data Source Description") }}</mat-label>
                <mat-icon class="absolute top-[-1.6rem] p-2 text-2xl" [ngClass]="lang === 'en' ? 'right-0' : 'left-0'"
                    [matTooltip]="
                        t(
                            'A human-readable explanation that helps SAI agents understand the context and content of the data source'
                        )
                    ">error_outline</mat-icon>
                <textarea matInput #descriptionField [formControlName]="'description'" maxlength="5000" [placeholder]="
                        t('Write a brief description for your data source')
                    " [rows]="3" [spellcheck]="true" cdkTextareaAutosize></textarea>
                <mat-hint align="end">{{
                    form.get("description").value?.length || 0
                    }}/5000</mat-hint>
                <button type="button" class="absolute right-2 bottom-2 bg-primary text-sm px-3 py-1 rounded-lg shadow"
                    [disabled]="!canGenerateDescription" [class.bg-primary]="canGenerateDescription"
                    [class.bg-invalid]="!canGenerateDescription" [matTooltip]="
  canGenerateDescription
    ? t('Generate description using this data source')
    : t('Save the data source first to enable generation')
" (click)="generateDescription()">
                    {{ t('Generate Description') }}
                </button>

                <mat-error *ngIf="
        form.get('description')?.touched &&
        form.get('description')?.hasError('maxlength')
    ">
                    {{ t("Data source description must be less than 5000 characters") }}
                </mat-error> 
            </mat-form-field>

            <!-- "Writable by agent" is paused for now.
            <div
                *ngIf="
                    form.get('type')?.value === 'internal' &&
                    form.get('internalType')?.value === 'Unstructured Documents'
                "
                class="col-span-1 lg:col-span-3 add-ds-agent-write"
            >
                <div class="add-ds-agent-write__head">
                    <mat-slide-toggle
                        [formControlName]="'agentWritable'"
                        [color]="'primary'"
                        [aria-describedby]="'add-ds-agent-write-desc'"
                    >
                        <span class="add-ds-agent-write__label">{{ t("Writable by agent") }}</span>
                    </mat-slide-toggle>
                </div>
                <p id="add-ds-agent-write-desc" class="add-ds-agent-write__desc">
                    {{ t('Agents connected to this data source can save files from their chats into it, after the user confirms. Saved files are searchable by everyone with access to this data source.') }}
                </p>
            </div>
            -->

            <div
                *ngIf="
                    form.get('internalType')?.value !== 'Unstructured Documents' &&
                    form.get('externalType')?.value !== 'AWS_S3' &&
                    form.get('externalType')?.value !== 'GOOGLE_DRIVE'
                "
                class="col-span-1 lg:col-span-3 add-ds-joins"
            >
                <div class="add-ds-joins__header">
                    <div class="add-ds-joins__title-block">
                        <h3 class="add-ds-joins__title">{{ t("Tables Join Rules") }}</h3>
                        <p class="add-ds-joins__subtitle">
                            {{ t("Describe how tables in this data source relate to each other") }}
                        </p>
                    </div>
                    <button
                        *ngIf="data.type !== 'View'"
                        mat-flat-button
                        class="bg-primary add-ds-joins__add-btn"
                        type="button"
                        (click)="addJoin()"
                    >
                        <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                        <span>{{ t("Add New Join") }}</span>
                    </button>
                </div>

                <div
                    *ngIf="!joins.length"
                    class="add-ds-joins__empty"
                >
                    <mat-icon
                        class="add-ds-joins__empty-icon"
                        [svgIcon]="'heroicons_outline:link'"
                    ></mat-icon>
                    <p class="add-ds-joins__empty-text">{{ t("No join rules yet") }}</p>
                    <button
                        *ngIf="data.type !== 'View'"
                        mat-stroked-button
                        type="button"
                        (click)="addJoin()"
                    >
                        <mat-icon [svgIcon]="'heroicons_outline:plus'"></mat-icon>
                        <span>{{ t("Add New Join") }}</span>
                    </button>
                </div>

                <div
                    *ngIf="joins.length"
                    class="add-ds-joins__grid"
                    formArrayName="joins"
                >
                    <div
                        class="add-ds-joins__card sai-card"
                        *ngFor="let join of joins.controls; index as i; trackBy: trackByJoinIndex"
                    >
                        <div class="add-ds-joins__card-head">
                            <span class="add-ds-joins__badge sai-badge">
                                {{ t("Join") }} {{ i + 1 }}
                            </span>
                            <button
                                *ngIf="data.type !== 'View'"
                                mat-icon-button
                                type="button"
                                class="sai-card-action-btn"
                                [matTooltip]="t('Remove join')"
                                [attr.aria-label]="t('Remove join')"
                                (click)="removeJoin(i)"
                            >
                                <mat-icon [svgIcon]="'heroicons_outline:trash'"></mat-icon>
                            </button>
                        </div>
                        <mat-form-field
                            class="w-full add-ds-joins__field fuse-mat-dense"
                            subscriptSizing="dynamic"
                        >
                            <textarea
                                matInput
                                [formControlName]="i"
                                [placeholder]="t('Write join data resource')"
                                rows="4"
                                spellcheck="true"
                                cdkTextareaAutosize
                                cdkAutosizeMinRows="4"
                                cdkAutosizeMaxRows="8"
                            ></textarea>
                        </mat-form-field>
                    </div>
                </div>
            </div>

                </div>
            </div>

            <div
                *ngIf="data.type !== 'View'"
                class="add-data-sources-dialog-footer flex items-center gap-2 dialog-actions"
            >
                <div
                    class="add-data-sources-dialog-footer-actions flex justify-end gap-2 ml-auto sai-tour-field"
                    data-sai-tour="ds-field-submit"
                >
                    <button type="button" mat-button (click)="close()">
                        {{ t("Cancel") }}
                    </button>
                    <button
                        appDisableWhileLoading
                        type="button"
                        mat-flat-button
                        [disabled]="form.invalid"
                        [class.bg-primary]="!form.invalid"
                        [class.bg-invalid]="form.invalid"
                        (click)="save()"
                    >
                        {{ data.type === "Add" ? t("Add") : t("Save") }}
                    </button>
                </div>
            </div>

            <div
                *ngIf="data.type === 'View'"
                class="add-data-sources-dialog-footer flex items-center gap-2 dialog-actions"
            >
                <div class="add-data-sources-dialog-footer-actions flex justify-end gap-2 ml-auto">
                    <button
                        type="button"
                        mat-flat-button
                        class="bg-primary"
                        (click)="close()"
                    >
                        {{ t("Close") }}
                    </button>
                </div>
            </div>
        </form>
    </ng-container>
</div>